import { Box, Link, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import CONFIG from "@APP/config";
import { PAYMENT_PROVIDER } from "@APP/types";
import { MOBILE_FONT } from "@APP/styles/mobile";

/**
 * Lloyds (LBG) variant of the legal block.
 *
 * The approved designs show the LBG regulatory statement as a plain left
 * aligned paragraph, followed by the two legal links stacked underneath as
 * standalone underlined links — the base component renders them as a bulleted
 * list, which the designs do not use.
 */
// Lloyds-hosted legal pages take precedence over the payment provider's own
// (Moneyhub's), falling back only if a brand hasn't configured URLS.LLOYDS.
const LINKS = [
  {
    text: "Terms and Conditions",
    href:
      CONFIG.URLS.LLOYDS?.TERMS_AND_CONDITIONS_URL ??
      CONFIG.URLS.PAYMENT_PROVIDER.TERMS_AND_CONDITIONS_URL,
    id: "moneyhubTermsAndConditionsLink",
  },
  {
    text: "Privacy Policy",
    href: CONFIG.URLS.LLOYDS?.PRIVACY_URL ?? CONFIG.URLS.PAYMENT_PROVIDER.PRIVACY_POLICY_URL,
    id: "moneyhubPrivacyPolicyLink",
  },
];

// The mobile designs run the regulatory block at the small legal size.
const MOBILE_SMALL_SX = { fontSize: `${MOBILE_FONT.small}px` };

const MoneyhubLegalInfo = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (CONFIG.PAYMENT_PROVIDER !== PAYMENT_PROVIDER.MONEYHUB) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      gap={isMobile ? "10px" : "1.5vh"}
      id={"moneyHubBox"}>
      {t("Common.MoneyhubText") ? (
        <Typography
          color="textPrimary"
          variant="body2"
          sx={isMobile ? MOBILE_SMALL_SX : undefined}
          id={"moneyHubText"}>
          {t("Common.MoneyhubText")}
        </Typography>
      ) : null}
      {LINKS.map(({ text, href, id }) => (
        <Link
          key={id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          id={id}
          color="textPrimary"
          sx={isMobile ? MOBILE_SMALL_SX : undefined}
          underline="always">
          {text}
        </Link>
      ))}
    </Box>
  );
};

export default MoneyhubLegalInfo;
